import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  TablePagination,
  Tooltip,
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  OpenInNew as OpenInNewIcon,
  GridView as BoardIcon,
  Palette as ThemeIcon,
  Remove as SpendIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { transactionsApi, billingApi, type PaymentTransaction, type CreditSpendRecord } from '../../api';

// Tab types for URL routing
export type TransactionTab = 'purchases' | 'usage';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`transaction-tabpanel-${index}`}
      aria-labelledby={`transaction-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

interface TransactionHistoryProps {
  activeTab?: TransactionTab;
  onTabChange?: (tab: TransactionTab) => void;
}

export function TransactionHistory({ activeTab = 'purchases', onTabChange }: TransactionHistoryProps) {
  // Convert tab name to index
  const tabIndex = activeTab === 'usage' ? 1 : 0;

  // Purchases state
  const [purchases, setPurchases] = useState<PaymentTransaction[]>([]);
  const [purchasesLoading, setPurchasesLoading] = useState(true);
  const [purchasesError, setPurchasesError] = useState<string | null>(null);
  const [purchasesTotal, setPurchasesTotal] = useState(0);
  const [purchasesPage, setPurchasesPage] = useState(0);
  const [purchasesRowsPerPage, setPurchasesRowsPerPage] = useState(10);

  // Credit spends state
  const [spends, setSpends] = useState<CreditSpendRecord[]>([]);
  const [spendsLoading, setSpendsLoading] = useState(true);
  const [spendsError, setSpendsError] = useState<string | null>(null);
  const [spendsTotal, setSpendsTotal] = useState(0);
  const [spendsPage, setSpendsPage] = useState(0);
  const [spendsRowsPerPage, setSpendsRowsPerPage] = useState(10);

  // Fetch purchases
  const fetchPurchases = useCallback(async () => {
    try {
      setPurchasesLoading(true);
      const result = await transactionsApi.listTransactions(
        purchasesRowsPerPage,
        purchasesPage * purchasesRowsPerPage
      );
      setPurchases(result.transactions || []);
      setPurchasesTotal(result.total || 0);
      setPurchasesError(null);
    } catch {
      setPurchasesError('Failed to load purchase history');
    } finally {
      setPurchasesLoading(false);
    }
  }, [purchasesPage, purchasesRowsPerPage]);

  // Fetch credit spends
  const fetchSpends = useCallback(async () => {
    try {
      setSpendsLoading(true);
      const result = await billingApi.getMyCreditSpends(
        spendsRowsPerPage,
        spendsPage * spendsRowsPerPage
      );
      setSpends(result.spends || []);
      setSpendsTotal(result.total || 0);
      setSpendsError(null);
    } catch {
      setSpendsError('Failed to load credit usage history');
    } finally {
      setSpendsLoading(false);
    }
  }, [spendsPage, spendsRowsPerPage]);

  useEffect(() => {
    fetchPurchases();
  }, [fetchPurchases]);

  useEffect(() => {
    fetchSpends();
  }, [fetchSpends]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    const newTab: TransactionTab = newValue === 1 ? 'usage' : 'purchases';
    onTabChange?.(newTab);
  };

  // Helper to format date
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Helper to get status chip props
  const getStatusChip = (status: PaymentTransaction['status']) => {
    switch (status) {
      case 'succeeded':
        return { label: 'Completed', color: 'success' as const, icon: <CheckCircleIcon /> };
      case 'failed':
        return { label: 'Failed', color: 'error' as const, icon: <ErrorIcon /> };
      case 'pending':
      default:
        return { label: 'Pending', color: 'warning' as const, icon: <PendingIcon /> };
    }
  };

  // Get icon for spend item type
  const getSpendIcon = (itemType: string) => {
    switch (itemType) {
      case 'board':
        return <BoardIcon fontSize="small" />;
      case 'theme':
        return <ThemeIcon fontSize="small" />;
      default:
        return <SpendIcon fontSize="small" />;
    }
  };

  // Get context label for spend
  const getSpendContextLabel = (context: string): string => {
    switch (context) {
      case 'create_board':
        return 'Board Created';
      case 'buy_theme':
        return 'Theme Purchased';
      default:
        return 'Credit Used';
    }
  };

  // Empty state component
  const EmptyState = ({ icon: Icon, title, subtitle }: { icon: React.ElementType; title: string; subtitle: string }) => (
    <Box sx={{ textAlign: 'center', py: 6 }}>
      <Icon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
      <Typography color="text.secondary">{title}</Typography>
      <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
    </Box>
  );

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <ReceiptIcon color="primary" sx={{ fontSize: 28 }} />
        <Typography variant="h6">Transaction History</Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        View your credit purchases and usage history.
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabIndex} onChange={handleTabChange} aria-label="transaction tabs">
          <Tab label="Credit Purchases" id="transaction-tab-0" />
          <Tab label="Credit Usage" id="transaction-tab-1" />
        </Tabs>
      </Box>

      {/* Credit Purchases Tab */}
      <TabPanel value={tabIndex} index={0}>
        {purchasesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : purchasesError ? (
          <Alert severity="error">{purchasesError}</Alert>
        ) : purchases.length === 0 ? (
          <EmptyState
            icon={AddIcon}
            title="No purchases yet"
            subtitle="Credit purchases will appear here"
          />
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Receipt</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {purchases.map((transaction) => {
                    const statusChip = getStatusChip(transaction.status);
                    const productName = transaction.product?.name || 'Credit Pack';
                    const creditsReceived = transaction.product?.metadata?.boardCredits;

                    return (
                      <TableRow key={transaction.id} hover>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(transaction.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {productName}
                          </Typography>
                          {creditsReceived && (
                            <Typography variant="caption" color="success.main">
                              +{creditsReceived} credit{creditsReceived !== 1 ? 's' : ''}
                            </Typography>
                          )}
                          {transaction.cardLast4 && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {transaction.cardBrand} •••• {transaction.cardLast4}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="medium">
                            ${(transaction.amount / 100).toFixed(2)}
                          </Typography>
                          {transaction.discountAmount > 0 && (
                            <Typography variant="caption" color="success.main">
                              -${(transaction.discountAmount / 100).toFixed(2)} discount
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={statusChip.label}
                            color={statusChip.color}
                            size="small"
                            icon={statusChip.icon}
                          />
                          {transaction.mode === 'test' && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Test
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {transaction.receiptUrl ? (
                            <IconButton
                              size="small"
                              href={transaction.receiptUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="View Receipt"
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          ) : (
                            <Typography variant="caption" color="text.disabled">
                              —
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={purchasesTotal}
              page={purchasesPage}
              onPageChange={(_e, newPage) => setPurchasesPage(newPage)}
              rowsPerPage={purchasesRowsPerPage}
              onRowsPerPageChange={(e) => {
                setPurchasesRowsPerPage(parseInt(e.target.value, 10));
                setPurchasesPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
            />
          </>
        )}
      </TabPanel>

      {/* Credit Usage Tab */}
      <TabPanel value={tabIndex} index={1}>
        {spendsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : spendsError ? (
          <Alert severity="error">{spendsError}</Alert>
        ) : spends.length === 0 ? (
          <EmptyState
            icon={SpendIcon}
            title="No credit usage yet"
            subtitle="Your credit usage history will appear here"
          />
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Item</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell align="right">Credits Used</TableCell>
                    <TableCell align="right">Balance</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {spends.map((spend) => (
                    <TableRow key={spend.id} hover>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(spend.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Tooltip title={spend.itemType}>
                            {getSpendIcon(spend.itemType)}
                          </Tooltip>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {spend.itemName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {spend.itemType === 'board' ? 'Board' : spend.itemType === 'theme' ? 'Theme' : spend.itemType}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getSpendContextLabel(spend.context)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          fontWeight="medium"
                          color="error.main"
                        >
                          -{spend.creditsSpent} credit{spend.creditsSpent !== 1 ? 's' : ''}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {spend.balanceAfter} credit{spend.balanceAfter !== 1 ? 's' : ''}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={spendsTotal}
              page={spendsPage}
              onPageChange={(_e, newPage) => setSpendsPage(newPage)}
              rowsPerPage={spendsRowsPerPage}
              onRowsPerPageChange={(e) => {
                setSpendsRowsPerPage(parseInt(e.target.value, 10));
                setSpendsPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
            />
          </>
        )}
      </TabPanel>
    </Paper>
  );
}
